import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  useMediaQuery,
  keyframes,
  CardContent,
  Button,
  InputAdornment
} from '@mui/material'
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, CheckCircle as CheckCircleIcon, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material'
import { ThemeProvider } from '@mui/material/styles'
import logo from '../assets/rounded_logo.svg'
import theme from '../styles/theme'
import TourSlotsDisplay from '../components/prospect/TourSlotsDisplay'
import PrimaryButton from '../components/PrimaryButton'
import CustomCard from '../components/CustomCard'
import union_logo from '../assets/Union.svg'
import axiosClient from '../services/axiosClient'
import SecondaryButton from '../components/SecondaryButton'
import ListingOnboardingForm from '../components/ListingOnboardingForm'
import GooglePlacesAutocomplete from '../components/GooglePlacesAutoComplete'
import PhoneInput from '../components/PhoneInput'
import PaginationControls from '../components/prospect/PaginationControls'
import type { TourRequestPrefillInput } from '../utils/tourRequestToListingPrefill'
import LandlordPhotoPicker from '../components/LandlordPhotoPicker'
import { listingService } from '../services/ListingService'
import { PENDING_LISTING_ID_KEY } from '../constants/pendingListing'
import type { PhotoMetadata } from '../types/listing_onboarding'

interface TourSlot {
  id: string
  date: string
  start_time_local: string
  end_time_local: string
  available_spots: number
  has_pending_request?: boolean
}

interface Prospect {
  id: string
  name: string
  tour_preference?: string
}

interface PublicTourRequestPageProps {
  prospect: Prospect
}

interface TourRequestFormData {
  listing_contact_email: string;
  listing_address: string;
  neighborhood: string;
  tour_giver_name: string;
  phone_number: string;
  rent: string;
  bedrooms: string;
  bathrooms: string;
  /** Placeholder; photos live in `pendingPhotos` state. */
  listing_photo_step: string;
  landlord_intro_message: string;
}

interface Question {
  id: string;
  title: string;
  type: 'text' | 'email' | 'address' | 'number' | 'phone' | 'long-text' | 'tour_slots' | 'counter' | 'listing_photos';
  field: keyof TourRequestFormData;
  placeholder?: string;
  subtext?: string;
  validation?: (value: any) => string | null;
  conditional?: (formData: TourRequestFormData) => boolean;
}

const SLOTS_PER_PAGE = 5
const PROXIMITY_CACHE_PREFIX = 'proximity-cache:'

interface ProximityPlace {
  id: number
  name: string
  enabled: boolean
  distance: string
}

interface ProximityGroup {
  category: string
  places: ProximityPlace[]
}

const normalizeAddress = (address: string) => address.trim().toLowerCase().replace(/\s+/g, ' ')
const getProximityCacheKey = (address: string) => `${PROXIMITY_CACHE_PREFIX}${normalizeAddress(address)}`

const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`

const questions: Question[] = [
  {
    id: 'tour_slots',
    title: 'Select a tour time',
    type: 'tour_slots',
    field: 'listing_contact_email', // This won't be used for tour slots
    validation: (value) => {
      if (!value) return 'Please select a tour time';
      return null;
    }
  },
  {
    id: 'listing_address',
    title: 'What\'s the address of your listing?',
    type: 'address',
    field: 'listing_address',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Please enter the listing address';
      return null;
    }
  },
  {
    id: 'neighborhood',
    title: 'What neighborhood is this in?',
    type: 'text',
    field: 'neighborhood',
    placeholder: 'Fells Point, Canton, Penn North',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Please enter the neighborhood';
      return null;
    }
  },
  {
    id: 'bedrooms',
    title: 'How many bedrooms?',
    type: 'counter',
    field: 'bedrooms',
    validation: (value) => {
      if (!value || Number(value) < 0) return 'Please enter a valid number';
      return null;
    }
  },
  {
    id: 'bathrooms',
    title: 'How many bathrooms?',
    type: 'counter',
    field: 'bathrooms',
    validation: (value) => {
      if (!value || Number(value) < 0) return 'Please enter a valid number';
      return null;
    }
  },
  {
    id: 'listing_contact_email',
    title: 'What\'s your email address?',
    type: 'email',
    field: 'listing_contact_email',
    subtext: 'Only used by us or the renter to contact you about the tour.',
    placeholder: 'your@email.com',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Please enter your email address';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return 'Please enter a valid email address';
      return null;
    }
  },
  {
    id: 'tour_giver_name',
    title: 'Who will be giving the tour?',
    type: 'text',
    field: 'tour_giver_name',
    placeholder: 'Jackie Robinson',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Please enter your name';
      return null;
    }
  },
  {
    id: 'phone_number',
    title: 'What\'s your phone number?',
    type: 'phone',
    subtext: 'Only used by us or the renter to contact you about the tour.',
    field: 'phone_number',
    placeholder: '(555) 123-4567',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Please enter your phone number';
      const digits = String(value).replace(/\D/g, '');
      if (digits.length !== 10) return 'Please enter a valid 10-digit phone number';
      return null;
    }
  },
  {
    id: 'rent',
    title: 'What\'s the monthly rent?',
    type: 'number',
    field: 'rent',
    validation: (value) => {
      if (!value || value.trim() === '') return 'Please enter the rent amount';
      const num = parseInt(value);
      if (isNaN(num) || num <= 0) return 'Please enter a valid rent amount';
      return null;
    }
  },
  {
    id: 'listing_photos',
    title: 'Up to 5 photos',
    type: 'listing_photos',
    field: 'listing_photo_step',
    subtext: 'Add at least one photo. Tap the star on a photo to set it as your feature image.',
    validation: () => null
  },
  {
    id: 'landlord_intro_message',
    title: '', // Display title from getPublicTourQuestionTitle (prospect name)
    type: 'long-text',
    field: 'landlord_intro_message',
    subtext: 'Greate way to introduce yourself and your listing.',
    placeholder: 'Hi there, can\'t wait for you to tour the home. I think you will love it!',
    validation: () => {
      return null; // Optional field
    }
  }
]

function getPublicTourQuestionTitle(question: Question, prospect: Prospect): string {
  if (question.id === 'landlord_intro_message') {
    const n = prospect.name?.trim();
    return n ? `Any message for ${n}?` : 'Any message for the lead?'
  }
  return question.title
}

export default function PublicTourRequestPage({ prospect }: PublicTourRequestPageProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [slots, setSlots] = useState<TourSlot[]>([])
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TourSlot | null>(null)
  const [slotPage, setSlotPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showListingForm, setShowListingForm] = useState(false)
  const [tourSubmittedForListingPrefill, setTourSubmittedForListingPrefill] = useState<TourRequestPrefillInput | null>(null)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [pendingListingId, setPendingListingId] = useState<string | null>(null)
  const [pendingListingIdLoading, setPendingListingIdLoading] = useState(false)
  const [pendingListingIdError, setPendingListingIdError] = useState<string | null>(null)
  const [pendingPhotos, setPendingPhotos] = useState<PhotoMetadata[]>([])
  const [listingPhotosError, setListingPhotosError] = useState('')
  const listingPhotosHydratedFromServerRef = useRef(false)
  const proximityPrefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestPrefetchAddressRef = useRef('')

  // Form data
  const [formData, setFormData] = useState<TourRequestFormData>({
    listing_contact_email: '',
    listing_address: '',
    neighborhood: '',
    tour_giver_name: '',
    phone_number: '',
    rent: '',
    bedrooms: '1',
    bathrooms: '1',
    listing_photo_step: '',
    landlord_intro_message: ''
  })

  const isMobile = useMediaQuery('(max-width:600px)')
  const currentQuestion = questions[currentStep]
  const currentQuestionId = questions[currentStep]?.id
  const prospectLeadLabel = (prospect.name && prospect.name.trim()) ? prospect.name : 'this'

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        setLoading(true)
        const response = await axiosClient.get(`/request-tour/${prospect.id}/slots`)
        setSlots(response.data.slots)
      } catch (err) {
        console.error('Error fetching slots:', err)
        setError('Failed to load available tour slots')
      } finally {
        setLoading(false)
      }
    }

    fetchSlots()
  }, [prospect.id])

  useEffect(() => {
    // Save the current margin
    const originalOverflowX = document.body.style.overflowX
    document.body.style.overflowX = 'hidden'
    const originalMargin = document.body.style.margin
    // Set margin to 0
    document.body.style.margin = '0'

    // Restore on unmount
    return () => {
      document.body.style.margin = originalMargin
      document.body.style.overflowX = originalOverflowX
    }
  }, [])

  // Ensure a PendingListing for the photo step (same session key as listing onboarding for createV2)
  useEffect(() => {
    if (currentQuestionId !== 'listing_photos') {
      return
    }
    if (pendingListingId || pendingListingIdLoading) {
      return
    }
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(PENDING_LISTING_ID_KEY)
      if (stored) {
        setPendingListingId(stored)
        return
      }
    }
    setPendingListingIdLoading(true)
    setPendingListingIdError(null)
    listingService
      .createPendingListing()
      .then((id) => {
        setPendingListingId(id)
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(PENDING_LISTING_ID_KEY, id)
        }
      })
      .catch(() => {
        setPendingListingIdError('Could not prepare photo upload. Please check your connection and try again.')
      })
      .finally(() => {
        setPendingListingIdLoading(false)
      })
  }, [currentQuestionId, pendingListingId, pendingListingIdLoading])

  useEffect(() => {
    const address = formData.listing_address.trim()
    if (address.length < 5) {
      return
    }
    if (normalizeAddress(address) === normalizeAddress(latestPrefetchAddressRef.current)) {
      return
    }

    if (proximityPrefetchTimeoutRef.current) {
      clearTimeout(proximityPrefetchTimeoutRef.current)
    }

    proximityPrefetchTimeoutRef.current = setTimeout(() => {
      const currentAddress = formData.listing_address.trim()
      if (currentAddress.length < 5) {
        return
      }
      latestPrefetchAddressRef.current = currentAddress

      axiosClient.post('/api/v1/marketing_listing/fetch_proximity_data', {
        full_address: currentAddress
      })
        .then((response) => {
          if (typeof window === 'undefined') return
          const grouped = response.data?.grouped_places as ProximityGroup[] | undefined
          const raw = response.data?.places
          const places = Array.isArray(raw) ? raw : Array.isArray(response.data) ? response.data : null
          const payload = {
            fetchedAt: Date.now(),
            grouped_places: Array.isArray(grouped) ? grouped : null,
            places: Array.isArray(places) ? places : null,
          }
          sessionStorage.setItem(getProximityCacheKey(currentAddress), JSON.stringify(payload))
        })
        .catch(() => {
          // noop - prefetch should never block the tour flow
        })
    }, 500)

    return () => {
      if (proximityPrefetchTimeoutRef.current) {
        clearTimeout(proximityPrefetchTimeoutRef.current)
      }
    }
  }, [formData.listing_address])

  useEffect(() => {
    if (currentQuestionId !== 'listing_photos') {
      listingPhotosHydratedFromServerRef.current = false
      return
    }
    if (!pendingListingId || pendingListingIdLoading) {
      return
    }
    if (listingPhotosHydratedFromServerRef.current) {
      return
    }
    listingPhotosHydratedFromServerRef.current = true
    listingService
      .getPendingListingPhotos(pendingListingId)
      .then((remote) => {
        if (remote.length > 0) {
          setPendingPhotos(remote)
        }
      })
      .catch(() => { /* noop */ })
  }, [currentQuestionId, pendingListingId, pendingListingIdLoading])

  // Paginate slots
  const startIdx = slotPage * SLOTS_PER_PAGE
  const endIdx = startIdx + SLOTS_PER_PAGE
  const paginatedSlots = slots.slice(startIdx, endIdx)

  // Format time for display
  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':')
    const date = new Date()
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0)
    if (isNaN(date.getTime())) return "Invalid Date"
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    if (isNaN(date.getTime())) return "Invalid Date"
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleInputChange = (field: keyof TourRequestFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateCurrentStep = () => {
    if (!currentQuestion) return true

    // Special validation for tour slots
    if (currentQuestion.type === 'tour_slots') {
      if (!selectedTimeSlot) {
        setError('Please select a tour time')
        return false
      }
      return true
    }

    if (currentQuestion.type === 'listing_photos') {
      if (pendingListingIdLoading || !pendingListingId) {
        return false
      }
      if (pendingPhotos.length < 1) {
        return false
      }
      if (pendingPhotos.length > 5) {
        return false
      }
      if (!pendingPhotos.some((p) => p.isFeatured)) {
        return false
      }
      return true
    }

    const validation = currentQuestion.validation
    if (!validation) return true

    const error = validation(formData[currentQuestion.field])
    if (error) {
      setErrors(prev => ({ ...prev, [currentQuestion.field]: error }))
      return false
    }

    return true
  }

  const handleNext = () => {
    if (validateCurrentStep()) {
      setListingPhotosError('')
      if (currentStep < questions.length - 1) {
        setCurrentStep(currentStep + 1)
      } else {
        handleSubmit()
      }
    } else {
      if (currentQuestion?.type === 'listing_photos') {
        if (pendingListingIdLoading || !pendingListingId) {
          setListingPhotosError('Preparing photo upload. Please wait a moment.')
        } else if (pendingPhotos.length < 1) {
          setListingPhotosError('Add at least one photo (up to 5).')
        } else if (pendingPhotos.length > 5) {
          setListingPhotosError('You can have at most 5 photos. Remove one to continue.')
        } else if (!pendingPhotos.some((p) => p.isFeatured)) {
          setListingPhotosError('Select a feature photo (tap the star on an image).')
        } else {
          setListingPhotosError('Please check your photos and try again.')
        }
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      setError(null)

      const response = await axiosClient.post(`/request-tour/${prospect.id}`, {
        tour_slot_id: selectedTimeSlot?.id,
        listing_contact_email: formData.listing_contact_email,
        listing_address: formData.listing_address,
        neighborhood: formData.neighborhood,
        tour_giver_name: formData.tour_giver_name,
        phone_number: formData.phone_number,
        rent: parseInt(formData.rent),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        pending_listing_id: pendingListingId || undefined,
        landlord_intro_message: formData.landlord_intro_message
      })

      if (response.data.success) {
        setTourSubmittedForListingPrefill({
          listing_address: formData.listing_address,
          rent: String(formData.rent),
          bedrooms: String(formData.bedrooms),
          bathrooms: String(formData.bathrooms),
          phone_number: formData.phone_number,
          neighborhood: formData.neighborhood,
          pending_listing_id: pendingListingId || undefined
        })
        setCurrentStep(questions.length) // Move to success screen
      }
    } catch (err: any) {
      console.error('Error submitting tour request:', err)
      setError(err.response?.data?.error || 'Failed to submit tour request')
    } finally {
      setSubmitting(false)
    }
  }

  const renderQuestion = () => {
    if (!currentQuestion) return null

    const { type, field, placeholder } = currentQuestion
    const value = formData[field]
    const error = errors[field]

    switch (type) {
      case 'tour_slots':
        return (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : slots.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  We apologize, but the prospect hasn't set their availability yet.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Please check back later.
                </Typography>
              </Box>
            ) : (
              <>
                {prospect.tour_preference && (
                  <Box
                    sx={{
                      backgroundColor: '#FFF8E1',
                      border: '1px solid #FFE082',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      mb: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#F57C00',
                        fontWeight: 500,
                      }}
                    >
                      {prospect.name} prefers <strong>{prospect.tour_preference.replace('_', ' ')}</strong> tours
                    </Typography>
                  </Box>
                )}
                <TourSlotsDisplay
                  slots={paginatedSlots}
                  selectedSlotId={selectedTimeSlot?.id || null}
                  onSelectSlot={setSelectedTimeSlot}
                  formatDate={formatDate}
                  formatTime={formatTime}
                />
              </>
            )}
            {slots.length > SLOTS_PER_PAGE && (
              <PaginationControls
                onShowPrevious={() => setSlotPage(Math.max(0, slotPage - 1))}
                onShowMore={() => setSlotPage(slotPage + 1)}
                disablePrevious={slotPage === 0}
                disableMore={endIdx >= slots.length}
              />
            )}
          </Box>
        )

      case 'email':
        return (
          <TextField
            label="Email Address"
            type="email"
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            error={!!error}
            helperText={error}
            fullWidth
            sx={{ maxWidth: 400, mx: 'auto' }}
            placeholder={placeholder}
            FormHelperTextProps={{
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
        )

      case 'address':
        return (
          <GooglePlacesAutocomplete
            value={value}
            onChange={(address) => handleInputChange(field, address)}
            textFieldProps={{
              error: !!error,
              helperText: error,
              placeholder: placeholder || 'Enter property address',
              fullWidth: true,
              sx: { maxWidth: 400, mx: 'auto' },
              FormHelperTextProps: {
                sx: {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem',
                }
              }
            }}
          />
        )

      case 'phone':
        return (
          <Box sx={{ maxWidth: 400, width: '100%' }}>
            <PhoneInput
              value={value as string}
              onChange={(digits) => handleInputChange(field, digits)}
              error={!!error}
              helperText={error || undefined}
              placeholder={placeholder}
            />
          </Box>
        )

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            error={!!error}
            helperText={error}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            placeholder={placeholder}
            sx={{ maxWidth: 400, mx: 'auto' }}
            FormHelperTextProps={{
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
        )

      case 'long-text':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            error={!!error}
            helperText={error}
            placeholder={placeholder}
            sx={{ maxWidth: 400, mx: 'auto' }}
            FormHelperTextProps={{
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
        )

      case 'text':
        return (
          <TextField
            fullWidth
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            error={!!error}
            helperText={error}
            placeholder={placeholder}
            sx={{ maxWidth: 400, mx: 'auto' }}
            FormHelperTextProps={{
              sx: {
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
              }
            }}
          />
        )

      case 'listing_photos':
        return (
          <Box>
            {listingPhotosError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {listingPhotosError}
              </Typography>
            )}
            <LandlordPhotoPicker
              pendingListingId={pendingListingId || ''}
              photos={pendingPhotos}
              onPhotosChange={setPendingPhotos}
              isEnsuringId={pendingListingIdLoading}
              ensureIdError={pendingListingIdError}
            />
          </Box>
        )

      case 'counter':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <Button
              onClick={() => {
                const currentValue = Number(typeof value === 'string' ? value : '0') || 0;
                const newValue = Math.max(0, currentValue - 1);
                handleInputChange(field, String(newValue));
              }}
              disabled={Number(typeof value === 'string' ? value : '0') <= 0}
              sx={{
                minWidth: '40px',
                height: '40px',
                p: 0,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                color: 'black',
                mx: 2
              }}
            >
              <RemoveIcon />
            </Button>
            <TextField
              value={typeof value === 'string' ? value : '0'}
              onChange={(e) => handleInputChange(field, e.target.value)}
              error={!!error}
              helperText={error}
              sx={{
                width: '70px',
                '& input': { textAlign: 'center' }
              }}
              InputProps={{
                inputProps: { min: 0 },
              }}
              FormHelperTextProps={{
                sx: {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem',
                }
              }}
            />
            <Button
              onClick={() => {
                const currentValue = Number(typeof value === 'string' ? value : '0') || 0;
                handleInputChange(field, String(currentValue + 1));
              }}
              sx={{
                minWidth: '40px',
                height: '40px',
                p: 0,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                color: 'black',
                mx: 2
              }}
            >
              <AddIcon />
            </Button>
          </Box>
        )

      default:
        return null
    }
  }

  // Show success screen if we've completed all questions
  if (currentStep >= questions.length) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Box
            sx={{
              minHeight: '100vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2
            }}
          >
            <CustomCard
              sx={{
                maxWidth: 600,
                m: '0 auto',
                width: '97%',
                p: 1,
                textAlign: 'left',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardContent>
                {/* Tour Request Summary */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
                  <Box
                    component="img"
                    src={union_logo}
                    alt="Union Logo"
                    sx={{
                      width: '100px',
                      height: '100px',
                      animation: `${waveAnimation} 2s ease-in-out infinite`
                    }}
                  />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, mt: 1, ml: 2, fontWeight: 600, color: 'text.primary', textAlign: 'center' }}>
                  Tour request sent to {prospect.name}!
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
                 Requested Tour Time:
                </Typography>
                <Box sx={{
                  backgroundColor: '#e3f2fd',
                  border: '2px solid #1976d2',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  mb: 4,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                }}>
                  <Typography variant="body1" sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    fontSize: '1.1rem'
                  }}>
                    📅 {formatDate(selectedTimeSlot?.date || '')}, at {formatTime(selectedTimeSlot?.start_time_local || '')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 4, textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    Next Steps
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 400, color: 'text.primary' }}>
                    ✅ {prospect.name} will approve or deny your request.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 400, color: 'text.primary' }}>
                    📧 You will get notified via email of their decision.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 400, color: 'text.primary' }}>
                    🤞 When they approve  (they should), close them on the tour 
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    In the off chance they reject the request, you will find out why. We ask them.
                  </Typography>
                </Box>

                {/* Divider */}
                <Box sx={{
                  width: '100%',
                  height: '1px',
                  bgcolor: 'divider',
                  mx: 'auto',
                  mb: 4
                }} />

                {/* Pitch to Customers */}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Free for landlords
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    Fewer bad showings. More renters like {prospectLeadLabel}.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Your criteria, your schedule — we handle the rest.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                    Share your screening flow via link, DM, or text — works alongside any listing channel you already use.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <SecondaryButton onClick={() => window.open('https://heyyproperty.com/', '_blank')}>
                      Learn More
                    </SecondaryButton>
                    <PrimaryButton onClick={() => {
                      setShowListingForm(true)
                      setTimeout(() => {
                        const formElement = document.getElementById('onboarding-form')
                        if (formElement) {
                          formElement.scrollIntoView({ behavior: 'smooth' })
                        }
                      }, 100)
                    }}>
                      Create In Seconds
                    </PrimaryButton>
                  </Box>
                </Box>
              </CardContent>
            </CustomCard>
          </Box>
        </Box>

        {/* Listing Onboarding Form */}
        {showListingForm && (
          <ListingOnboardingForm
            isVisible={true}
            skipAvailability
            tourRequestPrefill={tourSubmittedForListingPrefill ?? undefined}
          />
        )}
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      {/* Persistent header with logo and title */}
      <Box
        sx={{
          width: '90%',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '1rem' : '2rem',
          borderBottom: '1px solid #eee',
          mb: 0.5,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Heyy"
          sx={{
            height: 40,
            mr: 2,
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
          {prospect.name}'s Availability
        </Typography>
      </Box>

      {/* Progress indicator */}
      <Box sx={{
        maxWidth: 600,
        mx: 'auto',
        px: 2,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Typography variant="body2" sx={{ color: 'grey.600', minWidth: 'fit-content' }}>
          Question {currentStep + 1} of {questions.length}
        </Typography>
        <Box sx={{
          flex: 1,
          height: 4,
          bgcolor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <Box sx={{
            width: `${((currentStep + 1) / questions.length) * 100}%`,
            height: '100%',
            bgcolor: '#fbbf24',
            transition: 'width 0.3s ease'
          }} />
        </Box>
      </Box>

      {/* Question Content */}
      <Box sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: isMobile ? '1rem' : '2rem',
      }}>
        {currentQuestion && (
          <Box sx={{
            maxWidth: 600,
            width: '100%',
            textAlign: 'center',
            px: 2
          }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: currentQuestion.subtext ? 1 : 4,
                color: 'grey.900'
              }}
            >
              {getPublicTourQuestionTitle(currentQuestion, prospect)}
            </Typography>
            {currentQuestion.subtext && (
              <Typography
                variant="body2"
                sx={{
                  color: 'grey.600',
                  mb: 4,
                  fontSize: '0.875rem',
                  fontStyle: 'italic'
                }}
              >
                {currentQuestion.subtext}
              </Typography>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                {error}
              </Alert>
            )}
            {renderQuestion()}
          </Box>
        )}

        {/* Navigation buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4,
          gap: { xs: 3, md: 6 },
          maxWidth: 600,
          width: '100%',
          px: 2
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={currentStep === 0}
            sx={{
              borderColor: 'grey.300',
              color: 'grey.700',
              '&:hover': {
                borderColor: 'grey.400',
                bgcolor: 'grey.50'
              }
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            endIcon={
              currentStep === questions.length - 1 ?
                <CheckCircleIcon sx={{ color: '#fbbf24' }} /> :
                <ArrowForwardIcon sx={{ color: '#fbbf24' }} />
            }
            onClick={handleNext}
            disabled={submitting}
            sx={{
              bgcolor: 'black',
              color: 'white',
              px: 3,
              '&:hover': {
                bgcolor: 'grey.900',
                color: 'white'
              },
              '&:disabled': {
                bgcolor: 'grey.400',
                color: 'grey.600'
              }
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                Submitting...
              </>
            ) : currentStep === questions.length - 1 ? (
              'Submit Request'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  )
}